import { useState } from 'react';
import { useNavigate, useLocation, Link, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { IoEyeOutline, IoEyeOffOutline, IoBriefcaseOutline, IoBusinessOutline, IoChatbubble, IoLogoApple } from 'react-icons/io5';
import { FcGoogle } from 'react-icons/fc';
import { signOut } from 'firebase/auth';
import { login, signInWithSocial, findUserByAuth, authorizeKakao } from '../../services/authService';
import { useAuth } from '../../context/AuthContext';
import { auth } from '../../firebase/config';
import ieumLogo from '../../assets/ieum-logo.png';
import ChangePasswordModal from './ChangePasswordModal';
import AlertModal from '../../components/common/AlertModal';
import LoadingOverlay from '../../components/common/LoadingOverlay';
import { getSafeSignupRedirect, isRealtorAutoConnectRedirect } from '../../utils/signupRedirect';

const isIOS = typeof navigator !== 'undefined' && /iPhone|iPad|iPod/i.test(navigator.userAgent);

const PartnerLoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { refreshSession } = useAuth();
  const isDev = process.env.NODE_ENV === 'development';
  const isPartnerRoute = location.pathname.startsWith('/partner');
  const devDefaults = isPartnerRoute
    ? { id: 'partner4@test.com', pw: 'test1234!' }
    : { id: 'realtor3@test.com', pw: 'test1234!' };
  const [loginId, setLoginId] = useState(isDev ? devDefaults.id : '');
  const [password, setPassword] = useState(isDev ? devDefaults.pw : '');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pwChangeUid, setPwChangeUid] = useState(null);
  const [alert, setAlert] = useState(null); // { type, title, message }
  const [socialLoading, setSocialLoading] = useState('');
  const realtorRedirectTarget = getSafeSignupRedirect(searchParams, '');

  const finishLogin = async (user) => {
    await refreshSession();
    const canReturnToRealtorAuto =
      isRealtorAutoConnectRedirect(realtorRedirectTarget) && ['admin', 'staff', 'realtor'].includes(user?.role);
    if (realtorRedirectTarget && (user?.role === 'realtor' || canReturnToRealtorAuto)) {
      window.location.assign(realtorRedirectTarget);
      return;
    }
    navigate('/', { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(loginId.trim(), password);
      if (result?.tempPassword) {
        setPwChangeUid(result.uid);
        return;
      }
      await finishLogin(result);
    } catch (err) {
      const code = err.code || '';
      if (code === 'auth/pending') {
        const av = err.autoVerification || {};
        if (av.status === 'ocr_auto_rejected' && av.reasonCode === 'OCR_DOCUMENT_INVALID') {
          setAlert({
            type: 'error',
            title: '사업자 등록증 확인 필요',
            message: '정상적인 사업자 등록증 파일을 등록해주세요.',
          });
        } else if (av.status === 'ocr_auto_rejected' && av.reasonCode === 'NTS_STATUS_INVALID') {
          setAlert({
            type: 'error',
            title: '국세청 확인 결과',
            message: av.reasonText || '국세청 검증에 실패했습니다.',
          });
        } else if (av.status === 'ocr_auto_rejected') {
          setAlert({
            type: 'error',
            title: '사업자 등록증 자동검증 실패',
            message: av.reasonText || '사업자 등록증 검증에 실패했습니다.',
          });
        } else {
          setAlert({
            type: 'info',
            title: '이음에서 승인을 검토 중입니다',
            message: '관리자 승인 후 로그인하실 수 있습니다.\n자세한 문의는 고객센터로 연락주세요.',
          });
        }
      } else if (code === 'auth/withdrawn') {
        setAlert({
          type: 'error',
          title: '탈퇴 처리된 계정입니다',
          message: '동일한 계정으로는 재가입이 불가합니다.\n자세한 문의는 고객센터로 연락주세요.',
        });
      } else if (code === 'auth/suspended') {
        setAlert({
          type: 'error',
          title: '계정이 정지되었습니다',
          message: '관리자에게 문의하세요.',
        });
      } else if (code === 'auth/too-many-requests') {
        setAlert({
          type: 'error',
          title: '시도가 너무 많습니다',
          message: '잠시 후 다시 시도해 주세요.',
        });
      } else {
        setAlert({
          type: 'error',
          title: '입력하신 정보가 일치하지 않습니다',
          message: '아이디가 존재하지 않거나 입력하신 정보를\n다시 한번 확인해 주시기 바랍니다',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSocial = async (provider) => {
    if (socialLoading) return;
    setSocialLoading(provider);
    try {
      const isApp = typeof window !== 'undefined' && !!window.ReactNativeWebView;
      if (provider === 'kakao' && !isApp) {
        authorizeKakao(realtorRedirectTarget);
        return;
      }
      const { authUid, email, displayName } = await signInWithSocial(provider);
      const lookup = await findUserByAuth(authUid);

      if (lookup.found) {
        const user = lookup.user;
        if (user.status === 'pending') {
          await signOut(auth);
          setAlert({
            type: 'info',
            title: '이음에서 승인을 검토 중입니다',
            message: '관리자 승인 후 로그인하실 수 있습니다.',
          });
          return;
        }
        if (user.status === 'suspended') {
          await signOut(auth);
          setAlert({
            type: 'error',
            title: '계정이 정지되었습니다',
            message: '관리자에게 문의하세요.',
          });
          return;
        }
        await finishLogin(user);
        return;
      }

      navigate('/social/link-phone', {
        state: { authUid, email, displayName, provider, returnTo: realtorRedirectTarget },
      });
    } catch (err) {
      if (err?.code === 'auth/popup-closed-by-user' || err?.code === 'auth/cancelled-popup-request') {
        return;
      }
      setAlert({
        type: 'error',
        title: '로그인에 실패했습니다',
        message: err?.message || '잠시 후 다시 시도해 주세요.',
      });
    } finally {
      setSocialLoading('');
    }
  };

  return (
    <Wrapper>
      {(loading || socialLoading) && <LoadingOverlay minimal message="로그인 중입니다..." />}
      <Inner>
        <BrandWrap>
          <LogoImg src={ieumLogo} alt="이음" />
          <BrandTitle>{isPartnerRoute ? '이음 파트너스 어드민' : '이음 공인중개사'}</BrandTitle>
          <BrandSub>
            {isPartnerRoute
              ? '고객과 파트너스를 단, 5초만에 연결해드려요.'
              : '공인중개사님, 이음과 함께 시작하세요.'}
          </BrandSub>
        </BrandWrap>

        <FormCol>
        <FormHeader>
          <FormTitle>로그인</FormTitle>
          <FormSub>권한이 부여된 계정으로 접속해 주세요.</FormSub>
        </FormHeader>
        <form onSubmit={handleSubmit}>
          <Field>
            <Label>이메일</Label>
            <Input
              type="text"
              placeholder="이메일을 입력해 주세요"
              value={loginId}
              onChange={(e) => setLoginId(e.target.value)}
              autoComplete="username"
              required
            />
          </Field>

          <Field>
            <Label>비밀번호</Label>
            <InputWrap>
              <Input
                type={showPw ? 'text' : 'password'}
                placeholder="비밀번호를 입력해 주세요"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                $hasIcon
                required
              />
              <EyeButton
                type="button"
                onClick={() => setShowPw((v) => !v)}
                aria-label={showPw ? '비밀번호 숨기기' : '비밀번호 표시'}
              >
                {showPw ? <IoEyeOffOutline size={20} /> : <IoEyeOutline size={20} />}
              </EyeButton>
            </InputWrap>
          </Field>

          <OptionRow>
            <ForgotLink to="/forgot-password">비밀번호 찾기</ForgotLink>
          </OptionRow>

          <SubmitButton type="submit" disabled={loading}>
            {loading ? '로그인 중...' : '로그인'}
          </SubmitButton>
        </form>

        <OrDivider><span>또는</span></OrDivider>

        <SocialList>
          <SocialBtn
            type="button"
            $variant="kakao"
            disabled={!!socialLoading}
            onClick={() => handleSocial('kakao')}
          >
            <IoChatbubble size={18} />
            <span>{socialLoading === 'kakao' ? '진행 중...' : '카카오 로그인'}</span>
          </SocialBtn>
          <SocialBtn
            type="button"
            $variant="google"
            disabled={!!socialLoading}
            onClick={() => handleSocial('google')}
          >
            <FcGoogle size={18} />
            <span>{socialLoading === 'google' ? '진행 중...' : '구글 로그인'}</span>
          </SocialBtn>
          {isIOS && (
            <SocialBtn
              type="button"
              $variant="apple"
              disabled={!!socialLoading}
              onClick={() => handleSocial('apple')}
            >
              <IoLogoApple size={18} />
              <span>{socialLoading === 'apple' ? '진행 중...' : '애플 로그인'}</span>
            </SocialBtn>
          )}
        </SocialList>

        <SignupSection>
          <SignupHint>회원가입/신청</SignupHint>
          <SignupRow>
            <SignupBtn type="button" onClick={() => navigate('/partner/signup')}>
              <IoBriefcaseOutline size={18} />
              <span>제휴 업체</span>
            </SignupBtn>
            <SignupBtn type="button" onClick={() => navigate('/realtor/signup')}>
              <IoBusinessOutline size={18} />
              <span>공인중개사</span>
            </SignupBtn>
          </SignupRow>
        </SignupSection>
        </FormCol>
      </Inner>

      {pwChangeUid && (
        <ChangePasswordModal
          uid={pwChangeUid}
          onSuccess={async () => {
            await refreshSession();
            setPwChangeUid(null);
            navigate('/', { replace: true });
          }}
        />
      )}

      {alert && (
        <AlertModal
          type={alert.type}
          title={alert.title}
          message={alert.message}
          onConfirm={() => setAlert(null)}
        />
      )}
    </Wrapper>
  );
};

export default PartnerLoginPage;

// ───── 스타일 ─────
const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.bg};

  @media (min-width: 1200px) {
    padding: 40px;
    background: #2f3542;
  }
`;

const Inner = styled.div`
  width: 100%;
  max-width: 400px;
  padding: 0 ${({ theme }) => theme.spacing.sm};

  @media (min-width: 1200px) {
    max-width: 1080px;
    padding: 64px 80px;
    background: #fff;
    border-radius: 16px;
    display: grid;
    grid-template-columns: 1fr 1px 1fr;
    column-gap: 80px;
    align-items: center;
    min-height: 560px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
    position: relative;

    &::before {
      content: '';
      grid-column: 2;
      align-self: stretch;
      background: ${({ theme }) => theme.colors.borderLight};
    }
  }
`;

const BrandWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 36px;
  padding-top: 8px;

  @media (min-width: 1200px) {
    grid-column: 1;
    margin-bottom: 0;
    padding-top: 0;
    gap: 20px;
  }
`;

const BrandTitle = styled.h1`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 16px 0 4px;

  @media (min-width: 1200px) {
    font-size: 22px;
    margin: 24px 0 0;
  }
`;

const BrandSub = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0 0 ${({ theme }) => theme.spacing.lg};

  @media (min-width: 1200px) {
    font-size: 15px;
    margin: 4px 0 0;
  }
`;

const LogoImg = styled.img`
  width: 140px;
  height: auto;
  object-fit: contain;

  @media (min-width: 1200px) {
    width: 200px;
  }
`;

const FormCol = styled.div`
  @media (min-width: 1200px) {
    grid-column: 3;
  }
`;

const FormHeader = styled.div`
  display: none;

  @media (min-width: 1200px) {
    display: block;
    margin-bottom: 28px;
  }
`;

const FormTitle = styled.h2`
  font-size: 22px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 6px;
`;

const FormSub = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
`;

const Field = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const Label = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const InputWrap = styled.div`
  position: relative;
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 16px;
  padding-right: ${({ $hasIcon }) => ($hasIcon ? '48px' : '16px')};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fontSize.base};
  color: ${({ theme }) => theme.colors.text};
  outline: none;
  background: ${({ theme }) => theme.colors.bgWhite};

  &::placeholder {
    color: ${({ theme }) => theme.colors.textLight};
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const EyeButton = styled.button`
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.textLight};
  display: flex;
  align-items: center;
  padding: 4px;
  cursor: pointer;
`;

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin: ${({ theme }) => theme.spacing.md} 0 ${({ theme }) => theme.spacing.lg};
`;

const ForgotLink = styled(Link)`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-decoration: none;

  &:hover { color: ${({ theme }) => theme.colors.text}; }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 16px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  border: none;
  border-radius: ${({ theme }) => theme.radius.md};
  cursor: pointer;

  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const SignupSection = styled.div`
  margin-top: ${({ theme }) => theme.spacing.xxl};
`;

const SignupHint = styled.div`
  text-align: center;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.md};

  strong {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: ${({ theme }) => theme.fontWeight.bold};
  }
`;

const SignupRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.md};
`;

const SignupBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: 14px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;

  &:hover { opacity: 0.9; }
`;

const OrDivider = styled.div`
  display: flex;
  align-items: center;
  margin: ${({ theme }) => theme.spacing.lg} 0 ${({ theme }) => theme.spacing.base};
  color: ${({ theme }) => theme.colors.textLight};
  font-size: 12px;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${({ theme }) => theme.colors.border};
  }

  & > span {
    padding: 0 14px;
  }
`;

const SocialList = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing.md};
`;

const SocialBtn = styled.button`
  width: 100%;
  padding: 14px;
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  border: 1px solid transparent;
  transition: opacity 0.15s;

  ${({ $variant }) => {
    if ($variant === 'kakao') return `
      background: #FEE500;
      color: #000000;
    `;
    if ($variant === 'google') return `
      background: #FFFFFF;
      color: #1F2937;
      border-color: #E5E7EB;
    `;
    if ($variant === 'apple') return `
      background: #000000;
      color: #FFFFFF;
    `;
    return '';
  }}

  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;
